import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router';
import Loader from '~/app/common/Loader';
import { route } from '~/app/helpers/route';
import usePageTitle from '~/app/hooks/usePageTitle';
import Issues from '~/issues/List';
import Services from '~/services/List';
import { useStoreActions, useStoreState } from '~/store';
import { MachineOppgaver, MachineOppgaverType } from '~/types';
import { TASK_ROUTES } from '../Router';
import useTranslation from '~/app/hooks/useTranslation';
import classes from "./style.module.scss";
import useAccessControl from '~/app/hooks/useAccessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import WorkOrder from '~/workorder/List';

export type DetailComponent = React.FC;

const Detail: DetailComponent = () => {

  const history = useHistory();
  const netWorkStatus = useStoreState(state => state.app.offline.status);
  const { id } = useParams<{ id: MachineOppgaver['id'] }>();
  const machine = useStoreState(state => state.machine.data);
  const MachineFetchAction = useStoreActions(actions => actions.machine.fetchMachine);
  const [loading, setLoading] = useState<boolean>(true);

  const matchIssue = useRouteMatch(TASK_ROUTES.issue);
  const matchService = useRouteMatch(TASK_ROUTES.service);
  const matchWorkorder = useRouteMatch(TASK_ROUTES.workorder);

  const toggleMachineOppgaver =
    (matchIssue && MachineOppgaverType.ISSUE) ||
    (matchService && MachineOppgaverType.SERVICE) ||
    (matchWorkorder && MachineOppgaverType.WORKORDER) ||
    MachineOppgaverType.ISSUE;

  const { t } = useTranslation('tasks');
  const { checkPermission } = useAccessControl();
  const readServiceDetailsPermission = checkPermission(PERMISSION_GROUPS.readServiceDetails);

  usePageTitle((machine.name || '').toUpperCase());

  useEffect(() => {
    if (netWorkStatus === 0 && id) {
      MachineFetchAction(id).then(() => {
        setLoading(false);
      });
    } else {
      history.push(route(TASK_ROUTES.list));
    }
  }, [MachineFetchAction, id, netWorkStatus, history]);

  const clickHandler = useCallback((type: MachineOppgaverType) => {
    const typeToRoute: Record<MachineOppgaverType, string> = {
      [MachineOppgaverType.ISSUE]: TASK_ROUTES.issue,
      [MachineOppgaverType.SERVICE]: TASK_ROUTES.service,
      [MachineOppgaverType.WORKORDER]: TASK_ROUTES.workorder
    };
    history.push(route(typeToRoute[type], { id }));
  }, [history, id]);

  return (
    <>
      {(
        loading
          ? <Loader size='32' />
          : (
            <>
              <div className={classes['header']}>
                <div className={`${classes['link']} ${toggleMachineOppgaver === MachineOppgaverType.ISSUE && classes['active']}`} onClick={() => clickHandler(MachineOppgaverType.ISSUE)}>
                    {t('issue')}
                </div>
                {readServiceDetailsPermission && <div className={`${classes['link']} ${toggleMachineOppgaver === MachineOppgaverType.SERVICE && classes['active']}`} onClick={() => clickHandler(MachineOppgaverType.SERVICE)}>
                    {t('service')}
                </div>}
                <div className={`${classes['link']} ${toggleMachineOppgaver === MachineOppgaverType.WORKORDER && classes['active']}`} onClick={() => clickHandler(MachineOppgaverType.WORKORDER)}>
                    {t('workorder')}
                </div>
              </div>
              {toggleMachineOppgaver === MachineOppgaverType.ISSUE
                ? <Issues fromTask={true} />
                : toggleMachineOppgaver === MachineOppgaverType.SERVICE
                  ? <Services fromTask={true} />
                  : toggleMachineOppgaver === MachineOppgaverType.WORKORDER
                    ? <WorkOrder machineView={true} />
                    : null
              } 
            </>
          )
      )}
    </>
  );

}

export default Detail;
